import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const CarModal = ({ isOpen, onClose, car, onSave }) => {
    const { isDark } = useTheme();
    const [owners, setOwners] = useState([]);
    const [formData, setFormData] = useState({
        owner_id: '',
        model: '',
        model_year: '',
        color: '',
        status: '',
        working_city: '',
        car_type_: '',
        service_type: '',
        date_registered: '',
        car_number: '',
        setup_and_registration_cost: '',
        car_image_link: '',
        document_link: '',
    });

    // Helper to generate default sales years
    const getDefaultSales = () => {
        const currentYear = new Date().getFullYear();
        const sales = [];
        for (let i = 0; i < 4; i++) {
            sales.push({ year: (currentYear + i).toString(), amount: 1025 });
        }
        return sales;
    };

    // Config state for model_blob
    const [config, setConfig] = useState({
        sales: getDefaultSales(),
        probation_period: 0,
        working_period: 936,
        expenses: {
            servicing: 150,
            admin_fees: 180,
            insurance: 210,
            road_worthy: 20
        }
    });

    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchOwners();
        }
    }, [isOpen]);

    useEffect(() => {
        if (car) {
            setFormData({
                owner_id: car.owner_id || '',
                model: car.model || '',
                model_year: car.model_year || '',
                color: car.color || '',
                status: car.status || '',
                working_city: car.working_city || '',
                car_type_: car.car_type || '',
                service_type: car.service_type || '',
                date_registered: car.date_registered || '',
                car_number: car.car_number || '',
                setup_and_registration_cost: car.setup_cost || '',
                car_image_link: car.car_image_link || '',
                document_link: car.document_link || '',
            });
            // Populate config if exists
            if (car.model_blob) {
                // Convert sales object to array if needed
                let salesArray = getDefaultSales();
                if (car.model_blob.sales) {
                    salesArray = Object.entries(car.model_blob.sales).map(([year, amount]) => ({
                        year,
                        amount
                    })).sort((a, b) => a.year.localeCompare(b.year));
                }

                setConfig({
                    sales: salesArray,
                    probation_period: car.model_blob.probation_period ?? 0,
                    working_period: car.model_blob.working_period ?? 936,
                    expenses: {
                        servicing: 150,
                        admin_fees: 180,
                        insurance: 210,
                        road_worthy: 20,
                        ...car.model_blob.expenses
                    }
                });
            } else {
                // Fallback if model_blob is null on existing car
                setConfig({
                    sales: getDefaultSales(),
                    probation_period: 0,
                    working_period: 936,
                    expenses: {
                        servicing: 150,
                        admin_fees: 180,
                        insurance: 210,
                        road_worthy: 20
                    }
                });
            }
        } else {
            setFormData({
                owner_id: '',
                model: '',
                model_year: '',
                color: '',
                status: '',
                working_city: '',
                car_type_: '',
                service_type: '',
                date_registered: '',
                car_number: '',
                setup_and_registration_cost: '',
                car_image_link: '',
                document_link: '',
            });
            // Reset config to defaults
            setConfig({
                sales: getDefaultSales(),
                probation_period: 0,
                working_period: 936,
                expenses: {
                    servicing: 150,
                    admin_fees: 180,
                    insurance: 210,
                    road_worthy: 20
                }
            });
        }
        setErrors([]);
    }, [car, isOpen]);

    const fetchOwners = async () => {
        try {
            const response = await fetch('/api/v1/cars/owners_list', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setOwners(data);
        } catch (error) {
            console.error('Error fetching owners:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);

        try {
            const url = car ? `/api/v1/cars/${car.id}` : '/api/v1/cars';
            const method = car ? 'PATCH' : 'POST';

            // Use FormData for file upload
            const formDataToSend = new FormData();

            // Append all form fields
            Object.keys(formData).forEach(key => {
                if (formData[key]) {
                    formDataToSend.append(`car[${key}]`, formData[key]);
                }
            });

            // Append each config field individually
            formDataToSend.append('car[model_blob][probation_period]', config.probation_period);
            formDataToSend.append('car[model_blob][working_period]', config.working_period);

            // Expenses
            Object.keys(config.expenses).forEach(key => {
                formDataToSend.append(`car[model_blob][expenses][${key}]`, config.expenses[key]);
            });

            // Sales (Convert array back to object map)
            config.sales.forEach(item => {
                if (item.year && item.amount) {
                    formDataToSend.append(`car[model_blob][sales][${item.year}]`, item.amount);
                }
            });

            // Append image file if selected
            if (imageFile) {
                formDataToSend.append('car[image]', imageFile);
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                body: formDataToSend,
            });

            const data = await response.json();

            if (response.ok) {
                onSave(data);
                onClose();
            } else {
                setErrors(data.errors || ['An error occurred']);
            }
        } catch (error) {
            console.error('Error saving car:', error);
            setErrors(['Failed to save car. Please try again.']);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleConfigChange = (section, key, value) => {
        if (section === 'root') {
            setConfig(prev => ({ ...prev, [key]: value }));
        } else if (section === 'expenses') {
            setConfig(prev => ({
                ...prev,
                expenses: {
                    ...prev.expenses,
                    [key]: value
                }
            }));
        }
    };

    // Handlers for sales array
    const handleSalesChange = (index, field, value) => {
        const newSales = [...config.sales];
        newSales[index][field] = value;
        setConfig(prev => ({ ...prev, sales: newSales }));
    };

    const addSalesYear = () => {
        const lastYear = config.sales.length > 0
            ? parseInt(config.sales[config.sales.length - 1].year)
            : new Date().getFullYear() - 1;

        setConfig(prev => ({
            ...prev,
            sales: [...prev.sales, { year: (lastYear + 1).toString(), amount: 1025 }]
        }));
    };

    const removeSalesYear = (index) => {
        const newSales = config.sales.filter((_, i) => i !== index);
        setConfig(prev => ({ ...prev, sales: newSales }));
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
        },
        modal: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            maxWidth: '800px', // Wider for more fields
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        },
        header: {
            padding: '1.5rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        title: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: isDark ? '#9ca3af' : '#6b7280',
            padding: '0.5rem',
        },
        body: {
            padding: '1.5rem',
        },
        formRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.25rem',
        },
        formGroup: {
            marginBottom: '1.25rem',
        },
        label: {
            display: 'block',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isDark ? '#d1d5db' : '#374151',
        },
        input: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#2d2d2d' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            boxSizing: 'border-box',
        },
        select: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#2d2d2d' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            boxSizing: 'border-box',
        },
        errorContainer: {
            background: isDark ? '#7f1d1d' : '#fee2e2',
            color: isDark ? '#fca5a5' : '#991b1b',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
        },
        errorList: {
            margin: 0,
            paddingLeft: '1.25rem',
        },
        footer: {
            padding: '1.5rem',
            borderTop: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
        },
        button: {
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            transition: '0.2s',
        },
        cancelButton: {
            background: isDark ? '#374151' : '#e5e7eb',
            color: isDark ? '#d1d5db' : '#374151',
        },
        saveButton: {
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
        },
        sectionTitle: {
            fontSize: '1.1rem',
            fontWeight: 'bold',
            marginTop: '1.5rem',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#60a5fa' : '#3b82f6',
        }
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h2 style={styles.title}>{car ? 'Edit Car' : 'Add New Car'}</h2>
                    <button onClick={onClose} style={styles.closeButton}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={styles.body}>
                        {errors.length > 0 && (
                            <div style={styles.errorContainer}>
                                <ul style={styles.errorList}>
                                    {errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Basic Info */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Owner *</label>
                            <select
                                name="owner_id"
                                value={formData.owner_id}
                                onChange={handleChange}
                                required
                                style={styles.select}
                            >
                                <option value="">Select owner...</option>
                                {owners.map((owner) => (
                                    <option key={owner.id} value={owner.id}>
                                        {owner.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={styles.formRow}>
                            <div>
                                <label style={styles.label}>Model *</label>
                                <input
                                    type="text"
                                    name="model"
                                    value={formData.model}
                                    onChange={handleChange}
                                    required
                                    style={styles.input}
                                    placeholder="e.g., Toyota Camry"
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Model Year *</label>
                                <input
                                    type="text"
                                    name="model_year"
                                    value={formData.model_year}
                                    onChange={handleChange}
                                    required
                                    style={styles.input}
                                    placeholder="e.g., 2020"
                                />
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            <div>
                                <label style={styles.label}>Color *</label>
                                <input
                                    type="text"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleChange}
                                    required
                                    style={styles.input}
                                    placeholder="e.g., Black"
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Car Number *</label>
                                <input
                                    type="text"
                                    name="car_number"
                                    value={formData.car_number}
                                    onChange={handleChange}
                                    required
                                    style={styles.input}
                                    placeholder="e.g., GR-1234-20"
                                />
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            <div>
                                <label style={styles.label}>Status *</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    required
                                    style={styles.select}
                                >
                                    <option value="">Select status...</option>
                                    <option value="BOUGHT">BOUGHT</option>
                                    <option value="ACTIVE">ACTIVE</option>
                                    <option value="INACTIVE">INACTIVE</option>
                                </select>
                            </div>
                            <div>
                                <label style={styles.label}>Car Type *</label>
                                <select
                                    name="car_type_"
                                    value={formData.car_type_}
                                    onChange={handleChange}
                                    required
                                    style={styles.select}
                                >
                                    <option value="">Select type...</option>
                                    <option value="SALON">SALON</option>
                                    <option value="SUV">SUV</option>
                                    <option value="VAN">VAN</option>
                                    <option value="PICKUP">PICKUP</option>
                                    <option value="TRUCK">TRUCK</option>
                                    <option value="MINIBUS">MINIBUS</option>
                                    <option value="COUPE">COUPE</option>
                                    <option value="HATCHBACK">HATCHBACK</option>
                                    <option value="CROSSOVER">CROSSOVER</option>
                                    <option value="WAGON">WAGON</option>
                                </select>
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            <div>
                                <label style={styles.label}>Service Type *</label>
                                <select
                                    name="service_type"
                                    value={formData.service_type}
                                    onChange={handleChange}
                                    required
                                    style={styles.select}
                                >
                                    <option value="">Select service...</option>
                                    <option value="UBER-TAXI-SERVICES">UBER-TAXI-SERVICES</option>
                                    <option value="RENTAL">RENTAL</option>
                                    <option value="PRIVATE">PRIVATE</option>
                                </select>
                            </div>
                            <div>
                                <label style={styles.label}>Working City</label>
                                <input
                                    type="text"
                                    name="working_city"
                                    value={formData.working_city}
                                    onChange={handleChange}
                                    style={styles.input}
                                    placeholder="e.g., Accra"
                                />
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            <div>
                                <label style={styles.label}>Date Registered</label>
                                <input
                                    type="date"
                                    name="date_registered"
                                    value={formData.date_registered}
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Setup Cost (GHC)</label>
                                <input
                                    type="number"
                                    name="setup_and_registration_cost"
                                    value={formData.setup_and_registration_cost}
                                    onChange={handleChange}
                                    style={styles.input}
                                    placeholder="e.g., 5000"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        {/* Links and Images */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Car Image</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setImageFile(e.target.files[0])}
                                style={styles.input}
                            />
                            {imageFile && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                                    Selected: {imageFile.name}
                                </div>
                            )}
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Car Image Link</label>
                            <input
                                type="url"
                                name="car_image_link"
                                value={formData.car_image_link}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="https://example.com/car-image.jpg"
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Document Link</label>
                            <input
                                type="url"
                                name="document_link"
                                value={formData.document_link}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="https://example.com/document.pdf"
                            />
                        </div>

                        {/* Configuration Section */}
                        <h3 style={styles.sectionTitle}>Configuration</h3>

                        <div style={styles.formRow}>
                            <div>
                                <label style={styles.label}>Probation Period (days the driver will be on probation for)</label>
                                <input
                                    type="number"
                                    value={config.probation_period}
                                    onChange={(e) => handleConfigChange('root', 'probation_period', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Working Period (Number of days they will work for)</label>
                                <input
                                    type="number"
                                    value={config.working_period}
                                    onChange={(e) => handleConfigChange('root', 'working_period', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                            <label style={{ ...styles.label, marginBottom: 0, color: isDark ? '#93c5fd' : '#2563eb' }}>Weekly Sales</label>
                            <button
                                type="button"
                                onClick={addSalesYear}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: isDark ? '#60a5fa' : '#3b82f6',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                }}
                            >
                                + Add Year
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                            {config.sales.map((item, index) => (
                                <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.75rem', alignItems: 'end' }}>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem', display: 'block' }}>Year</label>
                                        <input
                                            type="text"
                                            value={item.year}
                                            onChange={(e) => handleSalesChange(index, 'year', e.target.value)}
                                            style={styles.input}
                                            placeholder="Year"
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '0.25rem', display: 'block' }}>Weekly Amount</label>
                                        <input
                                            type="number"
                                            value={item.amount}
                                            onChange={(e) => handleSalesChange(index, 'amount', e.target.value)}
                                            style={styles.input}
                                            placeholder="Amount"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeSalesYear(index)}
                                        style={{
                                            ...styles.button,
                                            padding: '0.75rem',
                                            background: isDark ? '#7f1d1d' : '#fee2e2',
                                            color: isDark ? '#fca5a5' : '#991b1b',
                                            height: '42px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {config.sales.length === 0 && (
                                <div style={{ textAlign: 'center', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.875rem', padding: '1rem' }}>
                                    No sales years configured. Click "Add Year" to start.
                                </div>
                            )}
                        </div>

                        <label style={{ ...styles.label, marginTop: '1rem', color: isDark ? '#93c5fd' : '#2563eb' }}>Monthly Expenses</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div>
                                <label style={styles.label}>Servicing</label>
                                <input
                                    type="number"
                                    value={config.expenses.servicing}
                                    onChange={(e) => handleConfigChange('expenses', 'servicing', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Admin Fees</label>
                                <input
                                    type="number"
                                    value={config.expenses.admin_fees}
                                    onChange={(e) => handleConfigChange('expenses', 'admin_fees', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Insurance</label>
                                <input
                                    type="number"
                                    value={config.expenses.insurance}
                                    onChange={(e) => handleConfigChange('expenses', 'insurance', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Road Worthy</label>
                                <input
                                    type="number"
                                    value={config.expenses.road_worthy}
                                    onChange={(e) => handleConfigChange('expenses', 'road_worthy', e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={styles.footer}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ ...styles.button, ...styles.cancelButton }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{ ...styles.button, ...styles.saveButton }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            {loading ? 'Saving...' : car ? 'Update Car' : 'Create Car'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CarModal;
